import { useCallback, useEffect, useState } from "react";
import { isElectronKiosk } from "../utils/kioskRuntime";
import "./PWAInstallPrompt.css";

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
}

const DISMISS_KEY = "pwa-install-prompt-dismissed";
const INSTALLABLE_EVENT = "pwa-installable";
const INSTALLED_EVENT = "pwa-installed";

let deferredPrompt: BeforeInstallPromptEvent | null = null;

if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (event: Event) => {
    event.preventDefault();
    deferredPrompt = event as BeforeInstallPromptEvent;
    window.dispatchEvent(new Event(INSTALLABLE_EVENT));
  });

  window.addEventListener("appinstalled", () => {
    deferredPrompt = null;
    window.dispatchEvent(new Event(INSTALLED_EVENT));
  });
}

const isStandalone = (): boolean =>
  window.matchMedia("(display-mode: standalone)").matches ||
  window.matchMedia("(display-mode: fullscreen)").matches ||
  (window.navigator as Navigator & { standalone?: boolean }).standalone === true;

function waitForNativePrompt(timeoutMs = 8000): Promise<boolean> {
  if (deferredPrompt) return Promise.resolve(true);
  return new Promise((resolve) => {
    const done = (ok: boolean) => {
      window.removeEventListener(INSTALLABLE_EVENT, onReady);
      window.clearTimeout(timer);
      resolve(ok);
    };
    const onReady = () => done(true);
    const timer = window.setTimeout(() => done(Boolean(deferredPrompt)), timeoutMs);
    window.addEventListener(INSTALLABLE_EVENT, onReady);
  });
}

const PWAInstallPrompt = () => {
  const [canInstall, setCanInstall] = useState(deferredPrompt !== null);
  const [dismissed, setDismissed] = useState(false);
  const [installed, setInstalled] = useState(false);

  useEffect(() => {
    if (isElectronKiosk() || isStandalone()) {
      setDismissed(true);
      return undefined;
    }

    const syncInstallable = () => setCanInstall(deferredPrompt !== null);
    const onInstalled = () => {
      setInstalled(true);
      setCanInstall(false);
      setDismissed(false);
    };
    syncInstallable();
    window.addEventListener(INSTALLABLE_EVENT, syncInstallable);
    window.addEventListener(INSTALLED_EVENT, onInstalled);

    return () => {
      window.removeEventListener(INSTALLABLE_EVENT, syncInstallable);
      window.removeEventListener(INSTALLED_EVENT, onInstalled);
    };
  }, []);

  const handleInstall = useCallback(async () => {
    if (!deferredPrompt) {
      if (navigator.serviceWorker) {
        try {
          await navigator.serviceWorker.ready;
        } catch {
          /* ignore */
        }
      }
      await waitForNativePrompt();
    }
    if (!deferredPrompt) return;

    await deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    deferredPrompt = null;
    setCanInstall(false);
    if (choice.outcome === "accepted") {
      setInstalled(true);
    }
  }, []);

  const handleOpen = useCallback(() => {
    window.open(`${window.location.origin}/`, "_blank");
  }, []);

  const handleDismiss = useCallback(() => {
    if (!installed) {
      try {
        sessionStorage.setItem(DISMISS_KEY, "true");
      } catch {
        /* ignore */
      }
    }
    setDismissed(true);
    setInstalled(false);
  }, [installed]);

  if (isElectronKiosk() || isStandalone()) {
    return null;
  }

  if (installed && !dismissed) {
    return (
      <div className="pwa-install-banner" role="dialog" aria-label="Open app">
        <img
          src={`${process.env.PUBLIC_URL}/logo192.png`}
          alt=""
          className="pwa-install-icon"
        />
        <div className="pwa-install-content">
          <span className="pwa-install-title">App installed</span>
          <span className="pwa-install-text">
            Mosida Selfie Station was added to your device.
          </span>
        </div>
        <div className="pwa-install-actions">
          <button
            type="button"
            className="pwa-install-btn pwa-install-btn-dismiss"
            onClick={handleDismiss}
          >
            Close
          </button>
          <button
            type="button"
            className="pwa-install-btn pwa-install-btn-install"
            onClick={handleOpen}
          >
            Open app
          </button>
        </div>
      </div>
    );
  }

  if (dismissed) {
    return null;
  }

  return (
    <div className="pwa-install-banner" role="dialog" aria-label="Install app">
      <img
        src={`${process.env.PUBLIC_URL}/logo192.png`}
        alt=""
        className="pwa-install-icon"
      />
      <div className="pwa-install-content">
        <span className="pwa-install-title">Install Mosida Selfie Station</span>
        <span className="pwa-install-text">
          Add it to your home screen for a faster, full-screen experience.
        </span>
      </div>
      <div className="pwa-install-actions">
        <button
          type="button"
          className="pwa-install-btn pwa-install-btn-dismiss"
          onClick={handleDismiss}
        >
          Not now
        </button>
        <button
          type="button"
          className="pwa-install-btn pwa-install-btn-install"
          onClick={handleInstall}
        >
          Install
        </button>
      </div>
    </div>
  );
};

export default PWAInstallPrompt;
